/**
 * Sprint 1F — Branch Staffing Modal
 *
 * Mounts from SystemDataManagement.js when the user clicks "Staff" on a
 * row in the IMT Branches tab. Wraps the backend ``staff`` action on
 * ``IMTBranchViewSet`` (Sprint 1C):
 *
 *   POST /api/fuel-enquiries/branches/{id}/staff/
 *
 * The endpoint accepts ``branch_manager`` (active employee PK or null),
 * ``ops_team`` (PK list), ``exec_team`` (PK list) and the three
 * notification toggles, all atomically. Validates active-only via the
 * lazy ``_ActiveEmployeePKField`` on the backend.
 *
 * UI shape:
 *   - Branch Manager  — single-select via the chip + picker pattern
 *     (matches the visual chrome of the team sections, but writes a
 *     single PK).
 *   - Operations Team / Executive Team — multi-select via the SAME grid
 *     pattern as the supervisor-assignment modal in HMSAdminDashboard.js
 *     (search + Select All / Deselect All + clickable user cards with
 *     checkbox / initials avatar / name / email / position).
 *
 * The branch payload from the list endpoint gives us PK lists for
 * ``ops_team`` / ``exec_team`` (no employee blobs — see
 * IMTBranchSerializer). To render names we fetch the active
 * employee list once per modal open. PKs in the saved payload that are
 * no longer active (employee deactivated after being added to the branch)
 * are auto-pruned from local state once the active-employee fetch
 * resolves and a small inline notice is shown — the backend's
 * ``_ActiveEmployeePKField`` would otherwise reject the save.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../utils/axiosConfig';
import systemDataService from '../services/systemDataService';
import { getErrorMessage } from '../utils/errorMessages';
import fetchEveryPage, { normalizePage } from '../utils/fetchEveryPage';
import { useFlashMessage } from './FlashMessage';
import './BranchStaffingModal.css';

const employeeLabel = (emp) => {
  if (!emp) return '';
  const name = emp.full_name
    || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
    || emp.email
    || `Employee #${emp.id}`;
  const positionName = emp.position?.name || emp.position_name || '';
  if (positionName) return `${name} (${positionName})`;
  return name;
};

const BranchStaffingModal = ({ branch, onClose, onSaved }) => {
  const { showSuccess, showError } = useFlashMessage();
  const [employees, setEmployees] = useState([]);
  // How many active employees the SERVER says exist, and whether we hold all
  // of them. Both go on screen: a list that is short is otherwise
  // indistinguishable from a company with fewer people in it.
  const [employeeTotal, setEmployeeTotal] = useState(0);
  const [employeeListComplete, setEmployeeListComplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeeError, setEmployeeError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Local edit state — initialised from the branch payload, applied on Save.
  const [branchManager, setBranchManager] = useState(branch?.branch_manager ?? null);
  const [opsTeam, setOpsTeam] = useState(Array.isArray(branch?.ops_team) ? branch.ops_team : []);
  const [execTeam, setExecTeam] = useState(Array.isArray(branch?.exec_team) ? branch.exec_team : []);
  const [notifyManager, setNotifyManager] = useState(
    branch?.notify_branch_manager_on_new_enquiry !== false,
  );
  const [notifyOps, setNotifyOps] = useState(branch?.notify_ops_on_stage_advance !== false);
  const [notifyExec, setNotifyExec] = useState(branch?.notify_exec_on_terminal_stage !== false);

  // Branch Manager picker (single-select, kept as chip + picker for the
  // single-value semantics).
  const [openPicker, setOpenPicker] = useState(null); // 'manager' | null
  const [pickerSearch, setPickerSearch] = useState('');

  // Per-grid search (Ops / Exec sections use the supervisor-modal grid).
  const [opsSearch, setOpsSearch] = useState('');
  const [execSearch, setExecSearch] = useState('');

  // Selector visibility — start collapsed so the modal opens clean. The
  // "Selected" pill strip stays visible regardless so admins always see
  // who's currently assigned; only the search/bulk/grid editor hides.
  const [opsExpanded, setOpsExpanded] = useState(false);
  const [execExpanded, setExecExpanded] = useState(false);

  // One-shot notice surfaced when local state is auto-pruned of any PKs
  // that don't appear in the active-employee fetch (i.e. an employee was
  // deactivated after being added to the branch). Cleared on next save.
  const [prunedNotice, setPrunedNotice] = useState(null);

  const overlayRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      setEmployeeError(null);
      try {
        // NOTE: endpoint is /api/hms-employees/ — employees.urls is included at
        // /api/ (not /api/employees/) in backend/hms/urls.py. Matches the path
        // used by FuelEnquiryForm and FuelOfferGenerator.
        // This asked for `page_size: 200` and got 100: the endpoint clamps
        // page_size to 100 and says nothing about it. So the modal held the
        // first 100 active employees, the search box below filters that
        // already-shortened list in the browser, and employee 101 could not be
        // staffed onto a branch by any means. Worse, the auto-prune below then
        // read "not in my list" as "deactivated" and quietly stripped real,
        // active people off the branch with a notice that said so. Pull every
        // page, and record whether we actually got the lot.
        const { rows, total, truncated } = await fetchEveryPage(async (page) => {
          const response = await axiosInstance.get('/api/hms-employees/', {
            params: { active: 'true', page, page_size: 100 },
          });
          return normalizePage(response.data);
        });
        if (cancelled) return;
        setEmployees(rows);
        setEmployeeTotal(total);
        // `truncated` is the runaway guard biting; `rows.length < total` is the
        // endpoint refusing to hand over the rest. Either one means this is a
        // slice, and a slice must never be presented as the whole staff list.
        setEmployeeListComplete(!truncated && rows.length >= total);
      } catch (err) {
        if (cancelled) return;
        setEmployeeError(getErrorMessage(err, 'Could not load active employees.'));
        setEmployees([]);
        setEmployeeTotal(0);
        setEmployeeListComplete(false);
      } finally {
        if (!cancelled) setLoadingEmployees(false);
      }
    };
    fetchEmployees();
    return () => { cancelled = true; };
  }, []);

  // Index by PK for O(1) chip name lookup.
  const employeeById = useMemo(() => {
    const map = new Map();
    employees.forEach(e => map.set(e.id, e));
    return map;
  }, [employees]);

  // Once active employees finish loading, drop any PKs in local state that
  // aren't in the active set. Without this, the backend's
  // ``_ActiveEmployeePKField`` would reject the entire save with 400.
  // Tracks "ran once per fetch" via the loading flag transition to false.
  useEffect(() => {
    if (loadingEmployees || employees.length === 0) return;
    // Only safe on a COMPLETE list. On a short one "absent from the list" does
    // not mean "deactivated", and pruning would remove real staff from the
    // branch and tell the admin they had been deactivated -- a wrong statement
    // on screen, which is worse than a missing one.
    if (!employeeListComplete) return;
    const activeSet = new Set(employees.map(e => e.id));
    const prunedOps = opsTeam.filter(id => activeSet.has(id));
    const prunedExec = execTeam.filter(id => activeSet.has(id));
    const managerInactive = branchManager != null && !activeSet.has(branchManager);
    const opsRemoved = opsTeam.length - prunedOps.length;
    const execRemoved = execTeam.length - prunedExec.length;
    const totalRemoved = opsRemoved + execRemoved + (managerInactive ? 1 : 0);
    if (totalRemoved === 0) return;
    if (opsRemoved > 0) setOpsTeam(prunedOps);
    if (execRemoved > 0) setExecTeam(prunedExec);
    if (managerInactive) setBranchManager(null);
    const parts = [];
    if (managerInactive) parts.push('the Branch Manager');
    if (opsRemoved > 0) parts.push(`${opsRemoved} Ops member${opsRemoved === 1 ? '' : 's'}`);
    if (execRemoved > 0) parts.push(`${execRemoved} Exec member${execRemoved === 1 ? '' : 's'}`);
    setPrunedNotice(
      `${parts.join(', ')} were removed because the employee${totalRemoved === 1 ? '' : 's'} ` +
      `${totalRemoved === 1 ? 'has' : 'have'} been deactivated. Save to commit.`,
    );
  }, [loadingEmployees, employees, employeeListComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderChipLabel = (id) => {
    const emp = employeeById.get(id);
    if (emp) return employeeLabel(emp);
    return `Employee #${id} (deactivated)`;
  };

  // Generic match used by both the BM picker and the Ops/Exec grid search.
  const matchesTerm = (emp, term) => {
    if (!term) return true;
    const haystack = [
      emp.display_name,
      emp.full_name,
      emp.first_name,
      emp.last_name,
      emp.email,
      emp.position?.name,
      emp.position_name,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(term);
  };

  const filteredCandidates = (excludeIds) => {
    const exclusion = new Set(excludeIds);
    const term = pickerSearch.trim().toLowerCase();
    return employees.filter(e => !exclusion.has(e.id) && matchesTerm(e, term));
  };

  // Branch Manager picker (single-select).
  const openManagerPicker = () => { setPickerSearch(''); setOpenPicker('manager'); };
  const closePicker = () => { setOpenPicker(null); setPickerSearch(''); };
  const setManagerAndClose = (id) => { setBranchManager(id); closePicker(); };
  const clearManager = () => setBranchManager(null);

  // Ops/Exec grid (multi-select).
  const filteredOps = useMemo(() => {
    const term = opsSearch.trim().toLowerCase();
    return employees.filter(e => matchesTerm(e, term));
  }, [employees, opsSearch]);

  const filteredExec = useMemo(() => {
    const term = execSearch.trim().toLowerCase();
    return employees.filter(e => matchesTerm(e, term));
  }, [employees, execSearch]);

  const toggleOps = (id) =>
    setOpsTeam(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleExec = (id) =>
    setExecTeam(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // "Select All" applies to the *currently filtered* set so an active search
  // narrows the bulk action — same behaviour as the supervisor modal.
  const selectAllOps = () =>
    setOpsTeam(prev => Array.from(new Set([...prev, ...filteredOps.map(e => e.id)])));
  const deselectAllOps = () => {
    if (opsSearch.trim() === '') {
      setOpsTeam([]);
    } else {
      const visible = new Set(filteredOps.map(e => e.id));
      setOpsTeam(prev => prev.filter(id => !visible.has(id)));
    }
  };
  const selectAllExec = () =>
    setExecTeam(prev => Array.from(new Set([...prev, ...filteredExec.map(e => e.id)])));
  const deselectAllExec = () => {
    if (execSearch.trim() === '') {
      setExecTeam([]);
    } else {
      const visible = new Set(filteredExec.map(e => e.id));
      setExecTeam(prev => prev.filter(id => !visible.has(id)));
    }
  };

  const initialsFor = (emp) => {
    const a = (emp.first_name || '').charAt(0);
    const b = (emp.last_name || '').charAt(0);
    if (a || b) return `${a}${b}`.toUpperCase();
    return (emp.email || '?').charAt(0).toUpperCase();
  };

  const displayNameFor = (emp) =>
    emp.display_name
    || emp.full_name
    || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
    || emp.email
    || `Employee #${emp.id}`;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        branch_manager: branchManager || null,
        ops_team: opsTeam,
        exec_team: execTeam,
        notify_branch_manager_on_new_enquiry: notifyManager,
        notify_ops_on_stage_advance: notifyOps,
        notify_exec_on_terminal_stage: notifyExec,
      };
      await systemDataService.staffBranch(branch.id, payload);
      setPrunedNotice(null);
      showSuccess(`Staffing updated for ${branch.name}`);
      onSaved && onSaved();
    } catch (err) {
      const msg = getErrorMessage(err, 'Could not save branch staffing.');
      setError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (saving) return;
    if (e.target === overlayRef.current) {
      onClose && onClose();
    }
  };

  // Render via portal to document.body. The dashboard's `.admin-container`
  // sets `backdrop-filter: blur(10px)`, which (per CSS spec) creates a new
  // containing block for `position: fixed` descendants — without the portal
  // the overlay scopes to that container instead of the viewport, leaving
  // the page footer un-dimmed.
  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="bsm-overlay"
      onClick={handleOverlayClick}
    >
      <div className="bsm-modal" role="dialog" aria-modal="true" aria-label="Branch Staffing">
        <div className="bsm-header">
          <h2 className="bsm-title">Branch Staffing: {branch.name}</h2>
          <button
            type="button"
            className="bsm-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="bsm-body">
          {error && (
            <div className="bsm-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {employeeError && (
            <div className="bsm-error">
              <i className="fas fa-exclamation-circle"></i>
              {employeeError}
            </div>
          )}

          {prunedNotice && (
            <div className="bsm-warning">
              <i className="fas fa-info-circle"></i>
              {prunedNotice}
            </div>
          )}

          {/* Say how many people this modal is choosing from. The search box
              in each section filters the list already in the browser, so if
              the list is short the missing people are unreachable AND
              invisible -- the admin just sees "No matching employees." and
              concludes the person does not exist. */}
          {!loadingEmployees && !employeeError && (
            employeeListComplete ? (
              <p className="bsm-hint bsm-employee-count">
                Choosing from all {employeeTotal} active employees.
              </p>
            ) : (
              <div className="bsm-warning">
                <i className="fas fa-info-circle"></i>
                Showing {employees.length} of {employeeTotal} active employees —
                the rest could not be loaded, so someone you are looking for may
                not appear. Reopen this window to try again.
              </div>
            )
          )}

          {/* Branch Manager ---------------------------------------------- */}
          {/* Single-select but rendered with the same chip + picker chrome as
              the Ops / Exec sections for visual consistency. Selecting from
              the picker replaces the current manager; the chip's Remove
              clears it. */}
          <section className="bsm-section">
            <div className="bsm-section-header">
              <span className="bsm-section-label">Branch Manager</span>
              <button
                type="button"
                className="bsm-add-btn"
                onClick={openManagerPicker}
                disabled={loadingEmployees || saving}
              >
                {branchManager ? '+ Change Manager' : '+ Assign Manager'}
              </button>
            </div>

            {branchManager ? (
              <ul className="bsm-chips">
                <li className="bsm-chip">
                  <span className="bsm-chip-label">{renderChipLabel(branchManager)}</span>
                  <button
                    type="button"
                    className="bsm-chip-remove"
                    onClick={clearManager}
                    disabled={saving}
                    aria-label={`Remove ${renderChipLabel(branchManager)}`}
                  >
                    Remove
                  </button>
                </li>
              </ul>
            ) : (
              <>
                <p className="bsm-empty">No branch manager assigned.</p>
                {!loadingEmployees && (
                  <p className="bsm-hint">
                    New enquiries on this branch won't have a manager snapshot.
                  </p>
                )}
              </>
            )}

            {openPicker === 'manager' && (
              <div className="bsm-picker">
                <input
                  type="text"
                  className="bsm-picker-search"
                  placeholder="Search active employees…"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  autoFocus
                />
                <ul className="bsm-picker-list">
                  {filteredCandidates(branchManager ? [branchManager] : []).length === 0 ? (
                    <li className="bsm-picker-empty">No matching employees.</li>
                  ) : (
                    filteredCandidates(branchManager ? [branchManager] : []).map(emp => (
                      <li key={emp.id}>
                        <button
                          type="button"
                          className="bsm-picker-option"
                          onClick={() => setManagerAndClose(emp.id)}
                        >
                          {employeeLabel(emp)}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
                <div className="bsm-picker-footer">
                  <button
                    type="button"
                    className="bsm-picker-close"
                    onClick={closePicker}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Operations Team --------------------------------------------- */}
          {/* Multi-select grid mirroring the supervisor-assignment modal in
              HMSAdminDashboard.js — search + Select All / Deselect All over
              the *currently filtered* set + clickable cards. The card's
              selected state IS the membership in ``opsTeam`` — no separate
              "candidates" array, so toggling is a single PK in/out. */}
          <section className="bsm-section">
            <div className="bsm-section-header">
              <span className="bsm-section-label">Operations Team</span>
              <div className="bsm-section-actions">
                <span className="bsm-selection-count">
                  {opsTeam.length} selected
                </span>
                <button
                  type="button"
                  className="bsm-add-btn"
                  onClick={() => setOpsExpanded(v => !v)}
                  disabled={loadingEmployees || saving}
                  aria-expanded={opsExpanded}
                >
                  {opsExpanded ? 'Hide' : 'Manage'}
                </button>
              </div>
            </div>

            {/* Always-visible summary of who is currently assigned, so admins
                don't have to expand the selector to see the team. */}
            <div className="bsm-selected-strip" aria-label="Selected operations team members">
              {opsTeam.length === 0 ? (
                <span className="bsm-selected-empty">None selected.</span>
              ) : (
                opsTeam.map(id => {
                  const emp = employeeById.get(id);
                  const label = emp ? displayNameFor(emp) : `Employee #${id}`;
                  return (
                    <span key={`ops-pill-${id}`} className="bsm-selected-pill">
                      <span className="bsm-selected-pill-label">{label}</span>
                      <button
                        type="button"
                        className="bsm-selected-pill-remove"
                        onClick={() => toggleOps(id)}
                        disabled={saving}
                        aria-label={`Remove ${label}`}
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            {opsExpanded && (
              <>
                <input
                  type="text"
                  className="bsm-grid-search"
                  placeholder="Search by name, email, or position…"
                  value={opsSearch}
                  onChange={(e) => setOpsSearch(e.target.value)}
                  disabled={loadingEmployees || saving}
                />

                <div className="bsm-grid-bulk">
                  <button
                    type="button"
                    className="bsm-bulk-btn"
                    onClick={selectAllOps}
                    disabled={loadingEmployees || saving || filteredOps.length === 0}
                  >
                    Select {opsSearch.trim() ? 'Filtered' : 'All'}
                  </button>
                  <button
                    type="button"
                    className="bsm-bulk-btn"
                    onClick={deselectAllOps}
                    disabled={loadingEmployees || saving || opsTeam.length === 0}
                  >
                    Deselect {opsSearch.trim() ? 'Filtered' : 'All'}
                  </button>
                </div>

                <div className="bsm-grid-container">
                  {loadingEmployees ? (
                    <p className="bsm-grid-empty">
                      <i className="fas fa-spinner fa-spin"></i> Loading employees…
                    </p>
                  ) : filteredOps.length === 0 ? (
                    <p className="bsm-grid-empty">No matching employees.</p>
                  ) : (
                    <div className="bsm-grid">
                      {filteredOps.map(emp => {
                        const selected = opsTeam.includes(emp.id);
                        return (
                          <div
                            key={`ops-${emp.id}`}
                            className={`bsm-grid-item ${selected ? 'selected' : ''}`}
                            onClick={() => !saving && toggleOps(emp.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (saving) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleOps(emp.id);
                              }
                            }}
                          >
                            <div className="bsm-grid-checkbox-wrap">
                              <input
                                type="checkbox"
                                className="bsm-grid-checkbox"
                                checked={selected}
                                onChange={() => {}}
                                tabIndex={-1}
                              />
                              <span className="bsm-grid-checkbox-custom"></span>
                            </div>
                            <div className="bsm-grid-avatar">
                              <div className="bsm-grid-initials">{initialsFor(emp)}</div>
                            </div>
                            <div className="bsm-grid-info">
                              <div className="bsm-grid-name">{displayNameFor(emp)}</div>
                              <div className="bsm-grid-email">{emp.email}</div>
                              {emp.position?.name && (
                                <div className="bsm-grid-meta">{emp.position.name}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          {/* Executive Team ---------------------------------------------- */}
          <section className="bsm-section">
            <div className="bsm-section-header">
              <span className="bsm-section-label">Executive Team</span>
              <div className="bsm-section-actions">
                <span className="bsm-selection-count">
                  {execTeam.length} selected
                </span>
                <button
                  type="button"
                  className="bsm-add-btn"
                  onClick={() => setExecExpanded(v => !v)}
                  disabled={loadingEmployees || saving}
                  aria-expanded={execExpanded}
                >
                  {execExpanded ? 'Hide' : 'Manage'}
                </button>
              </div>
            </div>

            <div className="bsm-selected-strip" aria-label="Selected executive team members">
              {execTeam.length === 0 ? (
                <span className="bsm-selected-empty">None selected.</span>
              ) : (
                execTeam.map(id => {
                  const emp = employeeById.get(id);
                  const label = emp ? displayNameFor(emp) : `Employee #${id}`;
                  return (
                    <span key={`exec-pill-${id}`} className="bsm-selected-pill">
                      <span className="bsm-selected-pill-label">{label}</span>
                      <button
                        type="button"
                        className="bsm-selected-pill-remove"
                        onClick={() => toggleExec(id)}
                        disabled={saving}
                        aria-label={`Remove ${label}`}
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            {execExpanded && (
              <>
                <input
                  type="text"
                  className="bsm-grid-search"
                  placeholder="Search by name, email, or position…"
                  value={execSearch}
                  onChange={(e) => setExecSearch(e.target.value)}
                  disabled={loadingEmployees || saving}
                />

                <div className="bsm-grid-bulk">
                  <button
                    type="button"
                    className="bsm-bulk-btn"
                    onClick={selectAllExec}
                    disabled={loadingEmployees || saving || filteredExec.length === 0}
                  >
                    Select {execSearch.trim() ? 'Filtered' : 'All'}
                  </button>
                  <button
                    type="button"
                    className="bsm-bulk-btn"
                    onClick={deselectAllExec}
                    disabled={loadingEmployees || saving || execTeam.length === 0}
                  >
                    Deselect {execSearch.trim() ? 'Filtered' : 'All'}
                  </button>
                </div>

                <div className="bsm-grid-container">
                  {loadingEmployees ? (
                    <p className="bsm-grid-empty">
                      <i className="fas fa-spinner fa-spin"></i> Loading employees…
                    </p>
                  ) : filteredExec.length === 0 ? (
                    <p className="bsm-grid-empty">No matching employees.</p>
                  ) : (
                    <div className="bsm-grid">
                      {filteredExec.map(emp => {
                        const selected = execTeam.includes(emp.id);
                        return (
                          <div
                            key={`exec-${emp.id}`}
                            className={`bsm-grid-item ${selected ? 'selected' : ''}`}
                            onClick={() => !saving && toggleExec(emp.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (saving) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleExec(emp.id);
                              }
                            }}
                          >
                            <div className="bsm-grid-checkbox-wrap">
                              <input
                                type="checkbox"
                                className="bsm-grid-checkbox"
                                checked={selected}
                                onChange={() => {}}
                                tabIndex={-1}
                              />
                              <span className="bsm-grid-checkbox-custom"></span>
                            </div>
                            <div className="bsm-grid-avatar">
                              <div className="bsm-grid-initials">{initialsFor(emp)}</div>
                            </div>
                            <div className="bsm-grid-info">
                              <div className="bsm-grid-name">{displayNameFor(emp)}</div>
                              <div className="bsm-grid-email">{emp.email}</div>
                              {emp.position?.name && (
                                <div className="bsm-grid-meta">{emp.position.name}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          {/* Notifications ----------------------------------------------- */}
          {/* These toggles currently fire IN-APP notifications only (the bell
              icon / Notification Center). The email leg was deferred in
              Sprint 1G — see backend/notifications/helpers.create_bulk_notification
              and the PHASES.md note. Labels reworded to match reality. */}
          <section className="bsm-section">
            <span className="bsm-section-label">In-App Notifications</span>
            <ul className="bsm-toggles">
              <li>
                <label className="bsm-toggle">
                  <input
                    type="checkbox"
                    checked={notifyManager}
                    onChange={(e) => setNotifyManager(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Notify Branch Manager on new enquiry</span>
                </label>
              </li>
              <li>
                <label className="bsm-toggle">
                  <input
                    type="checkbox"
                    checked={notifyOps}
                    onChange={(e) => setNotifyOps(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Notify Ops Team on stage advancement</span>
                </label>
              </li>
              <li>
                <label className="bsm-toggle">
                  <input
                    type="checkbox"
                    checked={notifyExec}
                    onChange={(e) => setNotifyExec(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Notify Exec Team on terminal stage</span>
                </label>
              </li>
            </ul>
          </section>
        </div>

        <div className="bsm-footer">
          <button
            type="button"
            className="bsm-btn bsm-btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bsm-btn bsm-btn-primary"
            onClick={handleSave}
            disabled={saving || loadingEmployees}
          >
            {saving ? (
              <><i className="fas fa-spinner fa-spin"></i> Saving…</>
            ) : (
              'Save Staffing'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BranchStaffingModal;
